import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import { zodResolver } from '@hookform/resolvers/zod';
import { OrderDocument, OrderItemDocument } from '@models/order';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import { startTracking } from '@util/firestore/orders';
import { ALL_CARRIERS, carrierCodeSchema } from '@util/maps/carriers';
import { useAuth } from 'context/AuthContext';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import BaseModal from './BaseModal';

interface AddTrackingModalProps {
  isOpen: boolean;
  dismiss: () => void;
  order?: OrderDocument;
  order_item?: OrderItemDocument;
  onSuccess?: () => void;
}

const formSchema = z.object({
  carrier: carrierCodeSchema,
  trackingNumber: z.string(),
});

type Form = z.infer<typeof formSchema>;

function AddTrackingModal({
  isOpen,
  dismiss,
  order,
  order_item,
  onSuccess,
}: AddTrackingModalProps) {
  const {
    control,
    handleSubmit,
    watch,
    register,
    setError,
    formState: { errors },
  } = useForm<Form>({
    mode: 'onSubmit',
    resolver: zodResolver(formSchema),
    defaultValues: {
      carrier: order_item?.carrier || 'usps',
      trackingNumber: order_item?.tracking_number || '',
    },
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { userDoc } = useAuth();
  const form = watch();
  const queryClient = useQueryClient();

  const onSubmit = async () => {
    if (!userDoc || !order || !order_item) return;
    setIsSubmitting(true);
    const seller_id = order_item.seller_id;
    if (!seller_id) {
      setIsSubmitting(false);
      setError('trackingNumber', {
        type: 'manual',
        message: 'Seller not found',
      });
      return;
    }

    const { carrier, trackingNumber: trackingNo } = form;
    const res = await startTracking({
      carrier: carrier,
      trackingNo: trackingNo.toUpperCase(),
      orderId: order.id,
      sellerId: seller_id,
      productId: order_item.id,
    });
    if (res.status === 'error') {
      setError('trackingNumber', {
        type: 'manual',
        message: res.message,
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: ['order'],
      });
      queryClient.invalidateQueries({
        queryKey: ['orders'],
      });
      onSuccess?.();
      dismiss();
    }
    setIsSubmitting(false);
  };
  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={dismiss}
      title="Add Tracking"
      overflowType="visible"
    >
      <form
        className="flex min-w-[42rem] flex-col gap-[1.6rem] p-8"
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormLabel value="Carrier">
          <FormSelect
            name="carrier"
            control={control}
            options={ALL_CARRIERS.map((e) => ({
              value: e.code,
              label: e.name,
              id: e.code,
            }))}
            allowEmpty={false}
          ></FormSelect>
        </FormLabel>
        <FormLabel
          value="Tracking Number"
          required
          errorMessage={errors?.trackingNumber?.message}
        >
          <FormInput
            {...register('trackingNumber')}
            error={!!errors.trackingNumber}
          ></FormInput>
        </FormLabel>

        <div className="flex justify-end gap-[1.6rem]">
          <Button
            type="tertiary"
            text="Cancel"
            height="small"
            onClick={dismiss}
          />
          <Button
            type="secondary"
            text={
              order_item?.tracking_number ? 'Update Tracking' : 'Add Tracking'
            }
            height="small"
            buttonType="submit"
            loading={isSubmitting}
          />
        </div>
      </form>
    </BaseModal>
  );
}

export default AddTrackingModal;
